import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Database } from "@/lib/supabase/types";
import { useUserContext } from "@/contexts/user";
import { Slider } from "./ui/slider";
import { Label } from "./ui/label";
import { cn } from "@/lib/utils";
import { Bar, CartesianGrid, ComposedChart, XAxis, YAxis } from "recharts";
import {
    ChartContainer,
    ChartLegend,
    ChartTooltip,
    ChartTooltipContent,
    type ChartConfig,
} from "./ui/chart";

type TimeOfDay = Database["public"]["Enums"]["morning_or_evening"];
type Question = {
    id: Database["public"]["Tables"]["checkin_questions"]["Row"]["id"],
    question: Database["public"]["Tables"]["checkin_questions"]["Row"]["question"],
};
export type Rating = {
    questionId: Database["public"]["Tables"]["checkin_ratings"]["Row"]["question_id"];
    rating: Database["public"]["Tables"]["checkin_ratings"]["Row"]["rating"];
};

const sliderClasses: Record<string, { rangeClassName: string, thumbClassName: string }> = {
    "Mentalt": { rangeClassName: "bg-step-3", thumbClassName: "border-step-3" },
    "Emotionellt": { rangeClassName: "bg-step-2", thumbClassName: "border-step-2" },
    "Fysiskt": { rangeClassName: "bg-step-1", thumbClassName: "border-step-1" },
};

type ChartDataPoint = Record<string, string | number | null>;

const chartConfig: ChartConfig = {
    Mentalt: { label: "Mentalt", color: "hsl(162 54% 28%)" },
    Emotionellt: { label: "Emotionellt", color: "hsl(204 100% 25%)" },
    Fysiskt: { label: "Fysiskt", color: "hsl(10 41% 51%)" },
    average: { label: "Medel", color: "#ebc302" },
};

type CheckinProps = {
    // buttonText: string;
    isOpen?: boolean;
    timeOfDay: TimeOfDay;
    // meditationAudioUrl: string;
    // meditationButtonText: string;
    // meditationAudioTitle: string;
    onOpenChanged?: React.Dispatch<React.SetStateAction<boolean>>;
    onRatingsChanged?: (ratings: Rating[]) => void;
};
const Checkin = forwardRef<HTMLElement, CheckinProps>(function Checkin({
    // meditationButtonText, meditationAudioUrl, meditationAudioTitle, 
    // buttonText, 
    isOpen: isOpenProp,
    onRatingsChanged: ratingChangedCallback,
    timeOfDay,
    onOpenChanged
}, forwardedRef) {
    const [questions, setQuestions] = useState<Map<string, Question>>(null);
    const [ratings, setRatings] = useState<Map<string, Rating>>(new Map());
    const [showRatings, setShowRatings] = useState(false);
    const [historicalData, setHistoricalData] = useState<ChartDataPoint[]>([]);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 13);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());

    const toggleKey = (key: string) => setHiddenKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
    });

    const displayData = useMemo(() => {
        if (!questions || ratings.size === 0) return historicalData;

        const today = new Date().toISOString().split('T')[0];
        const todayEntry: ChartDataPoint = {
            date: today,
            displayDate: `${today.slice(8)}/${today.slice(5, 7)}`,
        };
        let total = 0, count = 0;
        for (const [questionId, rating] of ratings) {
            const questionName = questions.get(questionId)?.question;
            if (questionName) {
                todayEntry[questionName] = rating.rating;
                total += rating.rating;
                count++;
            }
        }
        if (count > 0) todayEntry.average = Math.round(total / count);

        const todayIndex = historicalData.findIndex(d => d.date === today);
        if (todayIndex >= 0) {
            const result = [...historicalData];
            result[todayIndex] = todayEntry;
            return result;
        }
        // Today not yet in history (no prior commit today): insert in sorted order
        const result = [...historicalData];
        const insertIdx = result.findIndex(d => (d.date as string) > today);
        if (insertIdx >= 0) result.splice(insertIdx, 0, todayEntry);
        else result.push(todayEntry);
        return result;
    }, [historicalData, ratings, questions]);
    const { toast } = useToast();
    const { user } = useUserContext();
    const currentDateRef = useRef<string>();

    const upsertInProgressRef = useRef(false);
    const upsertQueueRef = useRef<Map<Question["id"], Rating>>(new Map());

    const thisRef = useRef(null);

    const isOpen = isOpenProp ?? showRatings;

    const [avgRating, setAvgRating] = useState<number>(undefined);

    useEffect(() => {
        const ratingValues = [...ratings.values()];
        if (ratingValues.length < questions?.size) {
            setAvgRating(undefined);
            return;
        }

        const total = ratingValues.reduce((total, rating) => total + rating.rating, 0);
        const avg = total / ratingValues.length;
        const avgRounded = Math.round(avg);
        setAvgRating(avgRounded);
    }, [questions?.size, ratings]);

    useImperativeHandle(forwardedRef, () => thisRef.current as HTMLInputElement);

    useEffect(() => {
        const fetchQuestionsAndRatings = async () => {
            const { data: questionData, error: questionError } = await supabase
                .from("checkin_questions")
                .select("id, question")
                .order("order", { ascending: true });

            if (questionError) throw questionError;
            const questionsMap = new Map(questionData.map(question => [question.id, question]));
            setQuestions(questionsMap);

            const { data: ratingData, error: ratingError } = await supabase
                .from("todays_checkin")
                .select("rating, question_id, date")
                .match({
                    "time_of_day": timeOfDay,
                    "user_id": user.id,
                }); // .limit(3); // This is would be fine rn, but won't include it just in case the number of questions changes in the future

            if (ratingError) throw ratingError;

            currentDateRef.current = ratingData[0]?.date;
            setRatings(new Map(
                ratingData.map(rating => [
                    rating.question_id,
                    {
                        rating: rating.rating,
                        questionId: rating.question_id,
                    } as Rating
                ])
            ));

            ratingChangedCallback(ratingData.map(rating => ({
                questionId: rating.question_id,
                rating: rating.rating,
            })));

            const { data: histData, error: histError } = await supabase
                .from("checkin_ratings")
                .select("date, question_id, rating")
                .eq("time_of_day", timeOfDay)
                .eq("user_id", user.id)
                .order("date", { ascending: false });
            // .limit(10 * 3); // 90 days × 3 questions

            if (histError) {
                console.error(histError);
                return;
            }

            const byDate = new Map<string, Map<string, number>>();
            for (const row of histData) {
                if (!byDate.has(row.date)) byDate.set(row.date, new Map());
                byDate.get(row.date)!.set(row.question_id, row.rating);
            }

            const toDisplayDate = (date: string) => `${date.slice(8)}/${date.slice(5, 7)}`;

            const nextDateStr = (dateStr: string): string => {
                const [y, m, d] = dateStr.split('-').map(Number);
                const next = new Date(y, m - 1, d + 1);
                return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
            };

            const sortedEntries = [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b));
            const chartData: ChartDataPoint[] = [];

            if (sortedEntries.length > 0) {
                let current = sortedEntries[0][0];
                const last = sortedEntries[sortedEntries.length - 1][0];
                const dataMap = new Map(sortedEntries);

                while (current <= last) {
                    const ratingsByQuestion = dataMap.get(current);
                    if (ratingsByQuestion) {
                        const entry: ChartDataPoint = { date: current, displayDate: toDisplayDate(current) };
                        let total = 0;
                        let count = 0;
                        for (const [qId, rating] of ratingsByQuestion) {
                            const questionName = questionsMap.get(qId)?.question ?? qId;
                            entry[questionName] = rating;
                            total += rating;
                            count++;
                        }
                        if (count > 0) entry.average = Math.round(total / count);
                        chartData.push(entry);
                    } else {
                        chartData.push({ date: current, displayDate: toDisplayDate(current) });
                    }
                    if (current === last) break;
                    current = nextDateStr(current);
                }
            }

            setHistoricalData(chartData);
        };
        fetchQuestionsAndRatings();
    }, [ratingChangedCallback, timeOfDay, user.id]);

    const createRatingRow = ({ questionId, rating }: Rating) => {
        return {
            user_id: user.id,
            date: currentDateRef.current,
            question_id: questionId,
            rating: rating,
            time_of_day: timeOfDay,
        };
    };

    const onRatingChanged = (rating: Rating) => {
        const newRatings = new Map(ratings ?? []);
        newRatings.set(rating.questionId, rating);

        setRatings(newRatings);
        ratingChangedCallback([...newRatings.values()]);
    };

    const onCommitRating = (rating: Rating) => {

        upsertQueueRef.current.set(rating.questionId, rating);
        if (upsertInProgressRef.current) return;

        (async () => {
            upsertInProgressRef.current = true;

            do {
                const ratings = [...upsertQueueRef.current.values()];
                upsertQueueRef.current.clear();

                const rows = ratings.map(rating => createRatingRow(rating));

                const { data, error } = await supabase
                    .from("checkin_ratings")
                    .upsert(rows,
                        {
                            defaultToNull: false,
                        }
                    )
                    .select("date")
                    .limit(1);

                if (error) {
                    toast({
                        title: "Lyckades inte spara ditt svar",
                        description: "Vänligen försök igen senare",
                        variant: "destructive"
                    });
                    console.error(error);
                    break;
                }

                currentDateRef.current = data?.[0]?.date;
            } while (upsertQueueRef.current.size > 0);

            upsertInProgressRef.current = false;
        })();
    };

    return (
        <div ref={thisRef} className="flex flex-col items-center mx-8">
            <Button
                variant={ratings && ratings?.size === questions?.size ? "success2" : "default"}
                className="rounded-full w-32 h-32 text-lg m-8 flex flex-col"
                onClick={() => {
                    setShowRatings(!isOpen);
                    onOpenChanged(!isOpen);
                }}
            >
                Dagens<br />balans
                {avgRating && <span>{avgRating}</span>}
            </Button>
            <div className={`overflow-hidden grid transition-[grid-template-rows] ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="min-h-0 text-center flex flex-col items-center">
                    <div className="w-full">
                        {questions && [...questions.values()].map(question => {
                            const currentRating = ratings?.get(question.id)?.rating ?? 0;
                            return (
                                <div className="my-8" key={question.id}>
                                    <div className="flex items-center justify-between gap-2 mb-4">
                                        <Label htmlFor={`checkin-slider-${question.id}`}>{question.question}</Label>
                                        <span className={cn(
                                            "text-muted-foreground text-sm",
                                            currentRating <= 0 && "invisible"
                                        )}>
                                            {currentRating}
                                        </span>
                                    </div>

                                    <Slider
                                        value={[currentRating]}
                                        onValueChange={(values) => {
                                            onRatingChanged({ questionId: question.id, rating: values?.[0] ?? 0 });
                                        }}
                                        onValueCommit={(values) => {
                                            onCommitRating({ questionId: question.id, rating: values?.[0] ?? 0 });
                                        }}
                                        rangeClassName={sliderClasses[question.question].rangeClassName}
                                        thumbClassName={sliderClasses[question.question].thumbClassName}
                                        id={`checkin-slider-${question.id}`}
                                        min={1}
                                        max={10}
                                        step={1}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-4 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-sm text-muted-foreground">Historik</p>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <input
                                    type="date"
                                    value={startDate}
                                    max={endDate}
                                    onChange={e => setStartDate(e.target.value)}
                                    className="bg-background border border-input rounded px-1.5 py-0.5 text-xs"
                                />
                                <span>–</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    min={startDate}
                                    onChange={e => setEndDate(e.target.value)}
                                    className="bg-background border border-input rounded px-1.5 py-0.5 text-xs"
                                />
                            </div>
                        </div>
                        <ChartContainer config={chartConfig} className="h-[200px] w-full">
                            <ComposedChart data={displayData.filter(d => d.date >= startDate && d.date <= endDate)} margin={{ left: 0, top: 8 }} barGap={2}>
                                <CartesianGrid vertical={false} />
                                <XAxis dataKey="displayDate" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                                <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} interval={0} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} width={24} />
                                <ChartTooltip content={<ChartTooltipContent />} />
                                <ChartLegend content={() => (
                                    <div className="flex items-center justify-center gap-4 pt-3 text-xs">
                                        {(["Mentalt", "Emotionellt", "Fysiskt", "average"] as const).map(key => {
                                            const color = chartConfig[key].color as string;
                                            const hidden = hiddenKeys.has(key);
                                            return (
                                                <button key={key} onClick={() => toggleKey(key)} className="flex items-center gap-1.5">
                                                    <div className="h-2 w-2 shrink-0 rounded-[2px] transition-colors" style={hidden
                                                        ? { backgroundColor: "transparent", outline: `1.5px solid ${color}` }
                                                        : { backgroundColor: color }
                                                    } />
                                                    <span className="text-muted-foreground">{chartConfig[key].label as string}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )} />
                                <Bar dataKey="Mentalt" type="monotone" fill="var(--color-Mentalt)" stroke="var(--color-Mentalt)" strokeWidth={1.5} strokeOpacity={1} isAnimationActive={false} hide={hiddenKeys.has("Mentalt")} />
                                <Bar dataKey="Emotionellt" type="monotone" fill="var(--color-Emotionellt)" stroke="var(--color-Emotionellt)" strokeWidth={1.5} strokeOpacity={1} isAnimationActive={false} hide={hiddenKeys.has("Emotionellt")} />
                                <Bar dataKey="Fysiskt" type="monotone" fill="var(--color-Fysiskt)" stroke="var(--color-Fysiskt)" strokeWidth={1.5} strokeOpacity={1} isAnimationActive={false} hide={hiddenKeys.has("Fysiskt")} />
                                <Bar dataKey="average" fill="var(--color-average)" stroke="var(--color-average)" strokeWidth={1.5} strokeOpacity={1} isAnimationActive={false} hide={hiddenKeys.has("average")} />
                            </ComposedChart>
                        </ChartContainer>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Checkin;